import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import PerfectScrollbar from 'react-perfect-scrollbar';
import styles from '../../../style';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TablePagination
} from '@material-ui/core';
import { FileCopyOutlined } from '@material-ui/icons/';
import { Skeleton } from '@material-ui/lab';
import { getVitalSigns } from 'services/soap/action';
import { connect } from 'react-redux';
import moment from 'moment';

// Vital sign config — label + field key in one place
const VITAL_FIELDS = [
  { label: 'BP',   key: 'bloodpressure' },
  { label: 'Temp', key: 'temperature' },
  { label: 'HR',   key: 'heartrate' },
  { label: 'RR',   key: 'respiratoryrate' },
  { label: 'Wt',   key: 'weight' },
  { label: 'Ht',   key: 'height' },
  { label: 'BMI',  key: 'bodymassindex', format: val => Number(val).toFixed(2) },
];

// Skeleton loader — outside component to avoid re-creation on every render
const TableRowsLoader = ({ rowsNum = 6 }) => (
  <>
    {[...Array(rowsNum)].map((_, i) => (
      <TableRow key={i}>
        {/* date + 7 vitals + copy button = 9 cells */}
        {[...Array(9)].map((__, j) => (
          <TableCell key={j}>
            <Skeleton animation="wave" variant="text" />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const VSHx = props => {
  const { classes, patientId, vitalSigns, totalElements } = props;

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [fetching, setFetching] = useState(true);

  const fetchData = useCallback(async (pg, rows) => {
    try {
      setFetching(true);
      await props.getVitalSigns(patientId, pg, rows);
    } catch (error) {
      console.error('Failed to load vital signs:', error);
    } finally {
      setFetching(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (patientId) {
      fetchData(page, rowsPerPage);
    }
  }, [patientId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = event => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchData(0, newRowsPerPage);
  };

  const hasResults = Array.isArray(vitalSigns) && vitalSigns.length > 0;

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Scrollable table area */}
      <PerfectScrollbar style={{ flex: 1 }}>
        <div style={{ height: 340 }}>
          <Table
            className={classes.generalTable}
            style={{ tableLayout: 'fixed', width: '100%' }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  style={{ width: 90, whiteSpace: 'nowrap' }}
                >
                  Date
                </TableCell>
                {VITAL_FIELDS.map(f => (
                  <TableCell
                    key={f.key}
                    align="center"
                    style={{ width: 70, whiteSpace: 'nowrap' }}
                  >
                    {f.label}
                  </TableCell>
                ))}
                <TableCell style={{ width: 44 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {fetching ? (
                <TableRowsLoader rowsNum={10} />
              ) : hasResults ? (
                vitalSigns.map((item, idx) => (
                  <TableRow key={item.id ?? idx} hover>
                    <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                      {item.createdDate
                        ? moment(item.createdDate).format('MM/DD/YYYY')
                        : '—'}
                    </TableCell>
                    {VITAL_FIELDS.map(f => (
                      <TableCell key={f.key} align="center">
                        {item[f.key] ? (
                          <>
                            {/* <Typography
                              variant="caption"
                              style={{ display: 'block', color: 'rgba(0,0,0,0.45)', lineHeight: 1.2 }}
                            >
                              {f.label}
                            </Typography> */}
                            <Typography variant="body2">
                              {f.format ? f.format(item[f.key]) : item[f.key]}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.2)' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="center" style={{ padding: 4 }}>
                      <IconButton
                        size="small"
                        onClick={() => props.handleCopyVsHx(item)}
                        title="Copy vitals"
                      >
                        <FileCopyOutlined fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    style={{
                      textAlign: 'center',
                      color: 'rgba(0,0,0,0.4)',
                      fontStyle: 'italic'
                    }}
                  >
                    No vital information available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </PerfectScrollbar>

      {/* Pagination */}
      {hasResults && (
        <TablePagination
          backIconButtonProps={{ 'aria-label': 'Previous Page' }}
          component="div"
          className={classes.paginationWrap}
          count={totalElements ?? 0}
          nextIconButtonProps={{ 'aria-label': 'Next Page' }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 15]}
        />
      )}

      {/* Footer */}
      <Box style={{ textAlign: 'right'}}>
        <Button
          color="primary"
          onClick={props.onCancel}
          size="small"
          style={{ borderRadius: 4 }}
          variant="contained"
        >
          Close
        </Button>
      </Box>

    </Box>
  );
};

VSHx.propTypes = {
  className: PropTypes.string
};

const mapDispatchToProps = { getVitalSigns };

const mapStateToProps = state => ({
  vitalSigns: state.soap?.userVitalSigns?.content,
  totalElements: state.soap?.userVitalSigns?.totalElements
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(VSHx))
);